import { useEffect, useMemo, useRef } from "react";
import { journey, subscribeJourney } from "../journey/journeyState";
import { SECTIONS } from "../journey/sections";
import { prefersReducedMotion } from "../journey/quality";
import { gradeWeights } from "./grade";

/* ── Cinema ───────────────────────────────────────────────
   Fixed layer stack behind the journey. Every layer is painted
   once; the subscriber only touches transform/opacity from
   cached refs. Zero layout reads, zero WebGL, no idle loop. */

const BASE = "#050507";

/* Per-section color temperature (index-aligned with SECTIONS).
   Cold open, warm middle, acid close — felt, not guessed. */
const GRADES = [
  { color: "rgba(70,110,255,0.24)", at: "30% 40%" }, // top — cold blue
  { color: "rgba(236,231,221,0.12)", at: "65% 50%" }, // about — paper warm
  { color: "rgba(120,160,200,0.16)", at: "50% 30%" }, // skills — steel
  { color: "rgba(245,158,11,0.22)", at: "40% 60%" }, // experience — amber
  { color: "rgba(239,68,120,0.16)", at: "60% 45%" }, // projects — rose
  { color: "rgba(205,255,62,0.14)", at: "50% 50%" }, // contact — acid
  { color: "rgba(155,206,26,0.20)", at: "50% 70%" }, // outro — acid deep
];

/* Two soft discs; softness lives in the gradient stops (no filter). */
const LEAKS = [
  { size: 70, left: 15, top: -10, rate: 0.15, color: "rgba(120,140,255,0.10)", drift: "leak-drift-a" },
  { size: 55, left: 40, top: 45, rate: 0.3, color: "rgba(236,231,221,0.07)", drift: "leak-drift-b" },
];

const mod = (n, m) => ((n % m) + m) % m;

export default function Cinema() {
  const gradeRefs = useRef([]);
  const leakRefs = useRef([]);
  const barTopRef = useRef(null);
  const barBotRef = useRef(null);
  const reduced = useMemo(prefersReducedMotion, []);

  useEffect(() => {
    let box = 0; // current letterbox amount 0..1
    let target = 0;
    let raf = 0;

    const paintBars = () => {
      const s = box.toFixed(3);
      if (barTopRef.current) barTopRef.current.style.transform = `scaleY(${s})`;
      if (barBotRef.current) barBotRef.current.style.transform = `scaleY(${s})`;
    };

    // self-terminating relax loop: runs only while the bars are open
    const tick = () => {
      target *= 0.88;
      box += (target - box) * 0.14;
      if (box < 0.002 && target < 0.002) {
        box = 0;
        target = 0;
        paintBars();
        raf = 0;
        return;
      }
      paintBars();
      raf = requestAnimationFrame(tick);
    };

    const update = () => {
      const vw = window.innerWidth;
      const p = journey.progress;
      const tx = journey.horizontal ? journey.trackX : -p * window.innerHeight * 4;

      // grade crossfade
      const w = gradeWeights(p);
      for (let i = 0; i < w.length; i++) {
        const el = gradeRefs.current[i];
        if (el) el.style.opacity = w[i].toFixed(3);
      }

      // light leaks — wrapped parallax so they never run out
      const period = vw * 2.6;
      LEAKS.forEach((leak, i) => {
        const el = leakRefs.current[i];
        if (!el) return;
        const off = mod(tx * leak.rate, period) - period / 2;
        el.style.transform = `translate3d(${off.toFixed(1)}px,0,0)`;
      });

      // letterbox follows |velocity|
      if (!reduced) {
        target = Math.max(target, Math.min(Math.abs(journey.velocity), 1));
        if (!raf) raf = requestAnimationFrame(tick);
      }
    };

    const unsub = subscribeJourney(update);
    update();
    return () => {
      unsub();
      cancelAnimationFrame(raf);
    };
  }, [reduced]);

  return (
    <div
      className="fixed inset-0 z-0 overflow-hidden pointer-events-none"
      aria-hidden
      style={{ background: BASE }}
    >
      {/* grade layers — one per section, opacity crossfades */}
      {GRADES.map((g, i) => (
        <div
          key={SECTIONS[i].id}
          ref={(el) => (gradeRefs.current[i] = el)}
          className="absolute inset-0"
          style={{
            opacity: i === 0 ? 1 : 0,
            background: `radial-gradient(110% 90% at ${g.at}, ${g.color}, transparent 70%)`,
          }}
        />
      ))}

      {/* light leaks — parallax outer, idle-drift inner */}
      {LEAKS.map((leak, i) => (
        <div
          key={i}
          ref={(el) => (leakRefs.current[i] = el)}
          className="absolute will-change-transform"
          style={{
            left: `${leak.left}vw`,
            top: `${leak.top}vh`,
            width: `${leak.size}vw`,
            height: `${leak.size}vw`,
          }}
        >
          <div
            className={`w-full h-full rounded-full ${reduced ? "" : leak.drift}`}
            style={{ background: `radial-gradient(circle, ${leak.color} 0%, transparent 62%)` }}
          />
        </div>
      ))}

      {/* vignette */}
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(120% 100% at 50% 50%, transparent 55%, rgba(0,0,0,0.6))" }}
      />

      {/* letterbox bars — scaleY follows scroll speed */}
      <div
        ref={barTopRef}
        className="absolute left-0 right-0 top-0 h-[8vh] bg-black origin-top"
        style={{ transform: "scaleY(0)" }}
      />
      <div
        ref={barBotRef}
        className="absolute left-0 right-0 bottom-0 h-[8vh] bg-black origin-bottom"
        style={{ transform: "scaleY(0)" }}
      />
    </div>
  );
}
